import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { BaseDialog, BtnCancel } from '@components/dialog';
import { BtnPrimary } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { IcoCrop, IcoRotate } from '@components/icons';
import { ImageCropper, applyCrop } from './image-cropper';
import { showError } from '@components/app-error';

type ImageEditorProps = {
  file: File;
  previewUrl: string;
  onCancel: () => void;
  onConfirm: (editedFile?: File) => void;
};

type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function ImageDisplay({
  previewUrl,
  rotation,
  onLoad,
}: {
  previewUrl: string;
  rotation: number;
  onLoad: (image: HTMLImageElement) => void;
}) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);
    return () => window.removeEventListener('resize', updateContainerSize);
  }, []);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) {
      return;
    }

    const handleLoad = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      onLoad(img);
    };

    img.addEventListener('load', handleLoad);
    if (img.complete) {
      handleLoad();
    }

    return () => img.removeEventListener('load', handleLoad);
  }, [onLoad]);

  const getImageDisplaySize = () => {
    if (!imageSize || !containerSize) {
      return { width: '100%', height: '100%' };
    }

    const { width: imgW, height: imgH } = imageSize;
    const { width: containerW, height: containerH } = containerSize;

    const isRotated = rotation % 180 !== 0;
    const displayW = isRotated ? imgH : imgW;
    const displayH = isRotated ? imgW : imgH;

    const scaleX = containerW / displayW;
    const scaleY = containerH / displayH;
    const scale = Math.min(scaleX, scaleY, 1);

    return {
      width: `${displayW * scale}px`,
      height: `${displayH * scale}px`,
    };
  };

  const displaySize = getImageDisplaySize();

  return (
    <div ref={containerRef} class="w-full h-full flex items-center justify-center">
      <img
        ref={imageRef}
        src={previewUrl}
        style={{
          objectFit: 'contain',
          width: displaySize.width,
          height: displaySize.height,
          transform: `rotate(${rotation}deg)`,
        }}
        alt="Image preview"
      />
    </div>
  );
}

type ImageEditorState = {
  isLoading: boolean;
  rotation: number;
  imageEl: HTMLImageElement | null;
  isCropMode: boolean;
  cropArea: CropArea | null;
};

export function ImageEditor({ file, previewUrl, onCancel, onConfirm }: ImageEditorProps) {
  const intl = useIntl();
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [editorState, setEditorState] = useState<ImageEditorState>({
    isLoading: true,
    rotation: 0,
    imageEl: null,
    isCropMode: false,
    cropArea: null,
  });
  const handleImageLoad = useCallback((loadedImage: HTMLImageElement) => {
    setEditorState((s) => ({ ...s, imageEl: loadedImage, isLoading: false }));
  }, []);

  const handleRotate = useCallback(
    () => setEditorState((s) => ({ ...s, rotation: (s.rotation + 90) % 360 })),
    [],
  );

  const handleToggleCrop = useCallback(() => {
    setEditorState((s) => ({ ...s, isCropMode: !s.isCropMode }));
  }, []);

  const handleCropChange = useCallback((newCropArea: CropArea) => {
    setEditorState((s) => ({ ...s, cropArea: newCropArea }));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!editorState.imageEl) {
      return onConfirm();
    }

    try {
      let finalBlob: Blob;
      if (editorState.isCropMode && editorState.cropArea && imageContainerRef.current) {
        finalBlob = await applyCrop(
          editorState.imageEl,
          editorState.cropArea,
          imageContainerRef.current,
          editorState.rotation,
          file.type,
        );
      } else {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const { naturalWidth: w, naturalHeight: h } = editorState.imageEl;
        if (editorState.rotation % 180 === 0) {
          canvas.width = w;
          canvas.height = h;
        } else {
          canvas.width = h;
          canvas.height = w;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((editorState.rotation * Math.PI) / 180);
        ctx.drawImage(editorState.imageEl, -w / 2, -h / 2);
        finalBlob = await new Promise((res, rej) =>
          canvas.toBlob(
            (b) => (b ? res(b) : rej(new Error('Canvas toBlob failed'))),
            file.type,
            0.92,
          ),
        );
      }
      onConfirm(new File([finalBlob], file.name, { type: file.type }));
    } catch (error) {
      showError(error);
      onConfirm();
    }
  }, [editorState, file, onConfirm]);

  return (
    <BaseDialog onClose={onCancel} blurBg={false} contentWidth={true}>
      <div class="grid grid-rows-[auto_1fr_auto] bg-white rounded-lg shadow-xl w-full max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[calc(100vh-4rem)] overflow-hidden">
        <header class="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 class="font-semibold text-xl text-gray-900">{intl('Edit Image')}</h2>
          <p class="text-gray-500 text-sm mt-1 truncate">{file.name}</p>
        </header>
        <div class="p-6 min-h-0 relative">
          <div
            ref={imageContainerRef}
            class="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg relative overflow-hidden"
          >
            {editorState.isLoading && (
              <div class="absolute inset-0 flex items-center justify-center text-gray-500 bg-gray-50 z-10">
                <div class="text-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  {intl('Loading...')}
                </div>
              </div>
            )}
            <ImageDisplay
              previewUrl={previewUrl}
              rotation={editorState.rotation}
              onLoad={handleImageLoad}
            />
            {editorState.isCropMode && editorState.imageEl && imageContainerRef.current && (
              <ImageCropper
                imageElement={editorState.imageEl}
                containerElement={imageContainerRef.current}
                onCropChange={handleCropChange}
              />
            )}
          </div>
        </div>

        <footer class="p-4 md:p-6 border-t flex flex-col md:flex-row items-center gap-3">
          <button
            type="button"
            disabled={editorState.isLoading}
            class={`w-full md:w-auto justify-center px-4 py-2 rounded-md border flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-2 md:order-1 ${
              editorState.isCropMode
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            onClick={handleToggleCrop}
          >
            <IcoCrop class="size-4" />
            {intl('Crop')}
          </button>
          <button
            type="button"
            disabled={editorState.isLoading}
            class="w-full md:w-auto justify-center px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-3 md:order-2"
            onClick={handleRotate}
          >
            <IcoRotate class="size-4" />
            {intl('Rotate')}
          </button>
          <BtnCancel
            class="w-full md:w-auto justify-center px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 order-last md:order-3 md:ml-auto"
            onClick={onCancel}
          >
            {intl('Cancel')}
          </BtnCancel>
          <BtnPrimary
            class="rounded-md w-full md:w-auto justify-center order-1 md:order-4"
            disabled={editorState.isLoading}
            onClick={handleConfirm}
          >
            {intl('Apply Changes')}
          </BtnPrimary>
        </footer>
      </div>
    </BaseDialog>
  );
}
